import { useNavigate } from "react-router-dom";
import { LogOut, Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import MessageBanner from "@/components/MessageBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { APP_VERSION } from "@/config/version";

export default function DashboardLayout({ title, menuItems, currentView, onMenuClick, children, hideMessageBanner = false }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );

  // 折りたたみ可能なカテゴリ見出しの開閉状態（localStorageに保持）
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sidebarCollapsedSections") || "{}");
    } catch (e) {
      return {};
    }
  });
  const toggleCollapse = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem("sidebarCollapsedSections", JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };

  // スマホでドロワーを開いている間は背面(本画面)のスクロールをロック
  useEffect(() => {
    if (typeof document === "undefined") return;
    const isMobile = window.innerWidth < 768;
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  // メニュー選択時: スマホではドロワーを閉じる
  const handleMenuClick = (item) => {
    onMenuClick && onMenuClick(item);
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* モバイル用オーバーレイ背景 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 no-print overflow-x-hidden overflow-y-auto overscroll-contain
          fixed inset-y-0 left-0 z-40 w-60 transition-transform duration-300
          md:static md:h-auto md:overflow-y-visible md:overscroll-auto md:z-auto md:translate-x-0 md:flex-shrink-0 md:transition-[width]
          ${sidebarOpen ? "translate-x-0 md:w-60" : "-translate-x-full md:w-0"}`}
      >
        <div className="flex flex-col p-4 min-h-full">
          {/* モバイル用 閉じるボタン */}
          <div className="md:hidden flex justify-end -mt-1 -mr-1 mb-1">
            <button
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close"
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Logo */}
          <div className="mb-3 flex items-center justify-center py-2">
            <img
              src="/assets/yasamaru-logo.png"
              alt="やさまるポータル"
              className="w-full h-auto"
            />
          </div>
          {/* Title */}
          <div className="text-[11px] text-slate-500 mb-3 text-center font-medium tracking-wide">
            {title}
          </div>
          {/* Menu */}
          <nav className="space-y-1">
            {(() => {
              const rows = [];
              let section = null; // 現在の折りたたみセクションのキー（null=常時表示）
              menuItems.forEach((item, idx) => {
                if (item.type === "divider") return;
                if (item.type === "category") {
                  if (item.collapsible) {
                    section = item.label;
                    const isCollapsed = !!collapsed[item.label];
                    rows.push(
                      <button
                        key={`category-${idx}`}
                        type="button"
                        onClick={() => toggleCollapse(item.label)}
                        data-testid={`sidebar-section-toggle-${item.label}`}
                        aria-expanded={!isCollapsed}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4 first:mt-0 rounded-md hover:bg-slate-100 transition-colors"
                      >
                        <span className="truncate">{item.label}</span>
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                        )}
                      </button>
                    );
                  } else {
                    section = null;
                    rows.push(
                      <div key={`category-${idx}`} className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4 first:mt-0">
                        {item.label}
                      </div>
                    );
                  }
                  return;
                }
                // 折りたたみ中のセクションに属する項目は非表示
                if (section && collapsed[section]) return;
                const isActive = currentView === item.id;
                const Icon = item.icon;
                rows.push(
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item)}
                    data-testid={`menu-${item.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2.5 ${
                      isActive
                        ? "bg-emerald-50 text-emerald-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {Icon && (
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 ${
                          isActive ? "text-emerald-600" : (item.iconColor || "text-slate-500")
                        }`}
                      />
                    )}
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              });
              return rows;
            })()}
          </nav>
          {/* Version & Logout */}
          <div className="mt-6 pt-3 border-t border-slate-200 space-y-2">
            {/* Version */}
            <div className="px-3 py-1 text-xs text-slate-400 text-center">
              v{APP_VERSION}
            </div>
            {/* Logout */}
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2.5 text-slate-700 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4 flex-shrink-0 text-slate-500" />
              <span className="truncate">ログアウト</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header — slim toggle bar */}
        <header className="bg-white border-b border-slate-200 px-3 py-1.5 no-print sticky top-0 z-10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 p-4">
          {!hideMessageBanner && <MessageBanner />}
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
